import { useState, useEffect, useCallback } from 'react';
import {
  History,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Award,
  Trophy,
  BookOpen,
  GraduationCap,
  Send,
  X
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { reportMemberSessionExpired } from '../../services/memberApi';

import useConnectivity from '@/services/connectivityService';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json'
});

const BELT_OPTIONS = [
  { value: 'white', label: 'White' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'orange', label: 'Orange' },
  { value: 'green', label: 'Green' },
  { value: 'blue', label: 'Blue' },
  { value: 'purple', label: 'Purple' },
  { value: 'brown', label: 'Brown' },
  { value: 'black_1', label: '1st Degree Black' },
  { value: 'black_2', label: '2nd Degree Black' },
  { value: 'black_3', label: '3rd Degree Black' },
];

const EVENT_TYPES = [
  { value: 'kata', label: 'Kata' },
  { value: 'kumite', label: 'Kumite' },
  { value: 'kobudo', label: 'Kobudo' },
  { value: 'team_kata', label: 'Team Kata' },
  { value: 'other', label: 'Other' },
];

const PLACE_OPTIONS = [
  { value: '1', label: '1st Place' },
  { value: '2', label: '2nd Place' },
  { value: '3', label: '3rd Place' },
  { value: 'participated', label: 'Participated' },
];

// ============================================
// Status Badge
// ============================================
const StatusBadge = ({ status }) => {
  const variants = {
    pending: { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300', label: 'Pending Review' },
    approved: { className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300', label: 'Approved' },
    rejected: { className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300', label: 'Rejected' },
  };
  const variant = variants[status] || variants.pending;
  return <Badge className={variant.className}>{variant.label}</Badge>;
};

// ============================================
// Prior Experience Tab
// ============================================
const SubmissionErrorBanner = ({ message }) => (
  <p className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1">
    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {message}
  </p>
);

const parseSubmissionError = async (response) => {
  if (response.status === 401) {
    reportMemberSessionExpired();
    return 'Your session has expired. Please log in again.';
  }
  try {
    const data = await response.json();
    if (typeof data?.detail === 'string') return data.detail;
  } catch {
    /* response had no JSON body */
  }
  return `Submission failed (HTTP ${response.status}). Please try again.`;
};

const NETWORK_ERROR_MESSAGE = 'Network error — submission did not reach the server. Please check your connection and retry. Your entries are still here.';

const PriorExperienceTab = ({ studentId, submissions, onSubmitSuccess }) => {
  const { isOnline } = useConnectivity();
  const [entries, setEntries] = useState([
    { style: '', years_trained: '', highest_rank: '', school_name: '', notes: '' }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const addEntry = () => {
    setEntries([...entries, { style: '', years_trained: '', highest_rank: '', school_name: '', notes: '' }]);
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const handleSubmit = async () => {
    if (!isOnline) {
      setError('You are offline. Reconnect to submit history updates.');
      return;
    }
    const validEntries = entries.filter(e => e.style.trim());
    if (validEntries.length === 0) return;

    setSubmitting(true);
    setSuccess(false);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/history-submissions`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            submission_type: 'prior_experience',
            prior_experience: validEntries.map(e => ({
              ...e,
              years_trained: e.years_trained ? Number(e.years_trained) : null
            }))
          })
        }
      );
      if (response.ok) {
        setSuccess(true);
        setEntries([{ style: '', years_trained: '', highest_rank: '', school_name: '', notes: '' }]);
        onSubmitSuccess();
      } else {
        setError(await parseSubmissionError(response));
      }
    } catch (err) {
      console.error('Failed to submit prior experience:', err);
      setError(NETWORK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const priorExpSubmissions = submissions.filter(s => s.submission_type === 'prior_experience');

  return (
    <div className="space-y-4">
      {/* Existing submissions */}
      {priorExpSubmissions.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-stone-600 dark:text-stone-400">Previous Submissions</p>
          {priorExpSubmissions.map(sub => (
            <ExistingSubmissionCard key={sub.id} submission={sub} studentId={studentId} onWithdraw={onSubmitSuccess} />
          ))}
        </div>
      )}

      {/* New entry form */}
      <div className="space-y-3">
        {entries.map((entry, idx) => (
          <Card key={idx} className="border border-stone-200 dark:border-stone-700">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-stone-700 dark:text-stone-300">Experience #{idx + 1}</p>
                {entries.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Style / Art *</Label>
                  <Input
                    value={entry.style}
                    onChange={(e) => updateEntry(idx, 'style', e.target.value)}
                    placeholder="e.g., Shotokan Karate"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Years Trained</Label>
                  <Input
                    type="number"
                    min="0"
                    value={entry.years_trained}
                    onChange={(e) => updateEntry(idx, 'years_trained', e.target.value)}
                    placeholder="e.g., 3"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Highest Rank</Label>
                  <Input
                    value={entry.highest_rank}
                    onChange={(e) => updateEntry(idx, 'highest_rank', e.target.value)}
                    placeholder="e.g., Green Belt"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">School Name</Label>
                  <Input
                    value={entry.school_name}
                    onChange={(e) => updateEntry(idx, 'school_name', e.target.value)}
                    placeholder="e.g., Tiger Karate Academy"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={entry.notes}
                  onChange={(e) => updateEntry(idx, 'notes', e.target.value)}
                  placeholder="Any additional details..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={addEntry}>
          <Plus className="h-4 w-4 mr-1" /> Add Another
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !isOnline || !entries.some(e => e.style.trim())}
          title={!isOnline ? 'Reconnect to submit.' : undefined}
          className="bg-[#9B1B30] hover:bg-[#7d1626] text-white"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </div>

      {success && (
        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle className="h-4 w-4" /> Submitted successfully. Pending review.
        </p>
      )}
      {error && <SubmissionErrorBanner message={error} />}
    </div>
  );
};

// ============================================
// Promotion History Tab
// ============================================
const PromotionHistoryTab = ({ studentId, submissions, onSubmitSuccess }) => {
  const { isOnline } = useConnectivity();
  const [entries, setEntries] = useState([
    { from_belt: 'white', to_belt: 'yellow', approximate_date: '', notes: '' }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const addEntry = () => {
    setEntries([...entries, { from_belt: 'white', to_belt: 'yellow', approximate_date: '', notes: '' }]);
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const handleSubmit = async () => {
    if (!isOnline) {
      setError('You are offline. Reconnect to submit history updates.');
      return;
    }
    const validEntries = entries.filter(e => e.from_belt && e.to_belt);
    if (validEntries.length === 0) return;

    setSubmitting(true);
    setSuccess(false);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/history-submissions`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            submission_type: 'promotion_history',
            promotion_history: validEntries
          })
        }
      );
      if (response.ok) {
        setSuccess(true);
        setEntries([{ from_belt: 'white', to_belt: 'yellow', approximate_date: '', notes: '' }]);
        onSubmitSuccess();
      } else {
        setError(await parseSubmissionError(response));
      }
    } catch (err) {
      console.error('Failed to submit promotion history:', err);
      setError(NETWORK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const promotionSubmissions = submissions.filter(s => s.submission_type === 'promotion_history');

  return (
    <div className="space-y-4">
      {promotionSubmissions.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-stone-600 dark:text-stone-400">Previous Submissions</p>
          {promotionSubmissions.map(sub => (
            <ExistingSubmissionCard key={sub.id} submission={sub} studentId={studentId} onWithdraw={onSubmitSuccess} />
          ))}
        </div>
      )}

      <div className="space-y-3">
        {entries.map((entry, idx) => (
          <Card key={idx} className="border border-stone-200 dark:border-stone-700">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-stone-700 dark:text-stone-300">Promotion #{idx + 1}</p>
                {entries.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From Belt</Label>
                  <select
                    value={entry.from_belt}
                    onChange={(e) => updateEntry(idx, 'from_belt', e.target.value)}
                    className="w-full mt-1 px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-md bg-background text-foreground"
                  >
                    {BELT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">To Belt</Label>
                  <select
                    value={entry.to_belt}
                    onChange={(e) => updateEntry(idx, 'to_belt', e.target.value)}
                    className="w-full mt-1 px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-md bg-background text-foreground"
                  >
                    {BELT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Approximate Date</Label>
                  <Input
                    type="date"
                    value={entry.approximate_date}
                    onChange={(e) => updateEntry(idx, 'approximate_date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={entry.notes}
                  onChange={(e) => updateEntry(idx, 'notes', e.target.value)}
                  placeholder="Any details about this promotion..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={addEntry}>
          <Plus className="h-4 w-4 mr-1" /> Add Another
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !isOnline || !entries.some(e => e.from_belt && e.to_belt)}
          title={!isOnline ? 'Reconnect to submit.' : undefined}
          className="bg-[#9B1B30] hover:bg-[#7d1626] text-white"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </div>

      {success && (
        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle className="h-4 w-4" /> Submitted successfully. Pending review.
        </p>
      )}
      {error && <SubmissionErrorBanner message={error} />}
    </div>
  );
};

// ============================================
// Tournament History Tab
// ============================================
const TournamentHistoryTab = ({ studentId, submissions, onSubmitSuccess }) => {
  const { isOnline } = useConnectivity();
  const [tournaments, setTournaments] = useState([
    { name: '', approximate_date: '', location: '', events: [] }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const addTournament = () => {
    setTournaments([...tournaments, { name: '', approximate_date: '', location: '', events: [] }]);
  };

  const removeTournament = (index) => {
    if (tournaments.length <= 1) return;
    setTournaments(tournaments.filter((_, i) => i !== index));
  };

  const updateTournament = (index, field, value) => {
    const updated = [...tournaments];
    updated[index] = { ...updated[index], [field]: value };
    setTournaments(updated);
  };

  const addEvent = (tournamentIdx) => {
    const updated = [...tournaments];
    updated[tournamentIdx].events = [
      ...updated[tournamentIdx].events,
      { event_type: 'kata', place: 'participated', division: '' }
    ];
    setTournaments(updated);
  };

  const removeEvent = (tournamentIdx, eventIdx) => {
    const updated = [...tournaments];
    updated[tournamentIdx].events = updated[tournamentIdx].events.filter((_, i) => i !== eventIdx);
    setTournaments(updated);
  };

  const updateEvent = (tournamentIdx, eventIdx, field, value) => {
    const updated = [...tournaments];
    updated[tournamentIdx].events = [...updated[tournamentIdx].events];
    updated[tournamentIdx].events[eventIdx] = { ...updated[tournamentIdx].events[eventIdx], [field]: value };
    setTournaments(updated);
  };

  const handleSubmit = async () => {
    if (!isOnline) {
      setError('You are offline. Reconnect to submit history updates.');
      return;
    }
    const validTournaments = tournaments.filter(t => t.name.trim());
    if (validTournaments.length === 0) return;

    setSubmitting(true);
    setSuccess(false);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/history-submissions`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            submission_type: 'tournament_history',
            tournament_history: validTournaments
          })
        }
      );
      if (response.ok) {
        setSuccess(true);
        setTournaments([{ name: '', approximate_date: '', location: '', events: [] }]);
        onSubmitSuccess();
      } else {
        setError(await parseSubmissionError(response));
      }
    } catch (err) {
      console.error('Failed to submit tournament history:', err);
      setError(NETWORK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const tournamentSubmissions = submissions.filter(s => s.submission_type === 'tournament_history');

  return (
    <div className="space-y-4">
      {tournamentSubmissions.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-stone-600 dark:text-stone-400">Previous Submissions</p>
          {tournamentSubmissions.map(sub => (
            <ExistingSubmissionCard key={sub.id} submission={sub} studentId={studentId} onWithdraw={onSubmitSuccess} />
          ))}
        </div>
      )}

      <div className="space-y-3">
        {tournaments.map((tournament, tIdx) => (
          <Card key={tIdx} className="border border-stone-200 dark:border-stone-700">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-stone-700 dark:text-stone-300">Tournament #{tIdx + 1}</p>
                {tournaments.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeTournament(tIdx)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Tournament Name *</Label>
                  <Input
                    value={tournament.name}
                    onChange={(e) => updateTournament(tIdx, 'name', e.target.value)}
                    placeholder="e.g., FL State Championships"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Approximate Date</Label>
                  <Input
                    type="date"
                    value={tournament.approximate_date}
                    onChange={(e) => updateTournament(tIdx, 'approximate_date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Input
                    value={tournament.location}
                    onChange={(e) => updateTournament(tIdx, 'location', e.target.value)}
                    placeholder="e.g., Tampa, FL"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Events */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Events</Label>
                  <Button variant="outline" size="sm" onClick={() => addEvent(tIdx)} className="h-7 text-xs">
                    <Plus className="h-3 w-3 mr-1" /> Add Event
                  </Button>
                </div>
                {tournament.events.length === 0 ? (
                  <p className="text-xs text-stone-400 italic">No events added. Click "Add Event" to record results.</p>
                ) : (
                  <div className="space-y-2">
                    {tournament.events.map((event, eIdx) => (
                      <div key={eIdx} className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded border">
                        <select
                          value={event.event_type}
                          onChange={(e) => updateEvent(tIdx, eIdx, 'event_type', e.target.value)}
                          className="px-2 py-1 text-xs border border-stone-300 dark:border-stone-600 rounded bg-background text-foreground"
                        >
                          {EVENT_TYPES.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <select
                          value={event.place}
                          onChange={(e) => updateEvent(tIdx, eIdx, 'place', e.target.value)}
                          className="px-2 py-1 text-xs border border-stone-300 dark:border-stone-600 rounded bg-background text-foreground"
                        >
                          {PLACE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <Input
                          value={event.division}
                          onChange={(e) => updateEvent(tIdx, eIdx, 'division', e.target.value)}
                          placeholder="Division"
                          className="text-xs h-7 flex-1"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeEvent(tIdx, eIdx)} className="text-red-500 h-7 w-7 p-0">
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={addTournament}>
          <Plus className="h-4 w-4 mr-1" /> Add Another Tournament
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !isOnline || !tournaments.some(t => t.name.trim())}
          title={!isOnline ? 'Reconnect to submit.' : undefined}
          className="bg-[#9B1B30] hover:bg-[#7d1626] text-white"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </div>

      {success && (
        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle className="h-4 w-4" /> Submitted successfully. Pending review.
        </p>
      )}
      {error && <SubmissionErrorBanner message={error} />}
    </div>
  );
};

// ============================================
// Seminar History Tab
// ============================================
const SeminarHistoryTab = ({ studentId, submissions, onSubmitSuccess }) => {
  const { isOnline } = useConnectivity();
  const newEntry = () => ({
    seminar_name: '',
    instructor: '',
    host_dojo: '',
    seminar_date: '',
    topics: '',
    hours: '',
    certificate_url: '',
    notes: '',
  });
  const [entries, setEntries] = useState([newEntry()]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const addEntry = () => setEntries([...entries, newEntry()]);
  const removeEntry = (idx) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((_, i) => i !== idx));
  };
  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx] = { ...updated[idx], [field]: value };
    setEntries(updated);
  };

  const handleSubmit = async () => {
    if (!isOnline) {
      setError('You are offline. Reconnect to submit history updates.');
      return;
    }
    const validEntries = entries
      .filter(e => e.seminar_name.trim())
      .map(e => ({
        seminar_name: e.seminar_name.trim(),
        instructor: e.instructor.trim() || null,
        host_dojo: e.host_dojo.trim() || null,
        seminar_date: e.seminar_date || null,
        topics: e.topics.trim() || null,
        hours: e.hours === '' ? null : parseFloat(e.hours),
        certificate_url: e.certificate_url.trim() || null,
        notes: e.notes.trim() || null,
      }));
    if (validEntries.length === 0) return;

    setSubmitting(true);
    setSuccess(false);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/history-submissions`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            submission_type: 'seminar_history',
            seminar_history: validEntries
          })
        }
      );
      if (response.ok) {
        setSuccess(true);
        setEntries([newEntry()]);
        onSubmitSuccess();
      } else {
        setError(await parseSubmissionError(response));
      }
    } catch (err) {
      console.error('Failed to submit seminar history:', err);
      setError(NETWORK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const seminarSubmissions = submissions.filter(s => s.submission_type === 'seminar_history');

  return (
    <div className="space-y-4">
      {seminarSubmissions.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-stone-600 dark:text-stone-400">Previous Submissions</p>
          {seminarSubmissions.map(sub => (
            <ExistingSubmissionCard key={sub.id} submission={sub} studentId={studentId} onWithdraw={onSubmitSuccess} />
          ))}
        </div>
      )}

      <div className="space-y-3">
        {entries.map((entry, idx) => (
          <Card key={idx} className="border border-stone-200 dark:border-stone-700">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-stone-700 dark:text-stone-300">Seminar #{idx + 1}</p>
                {entries.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div>
                <Label className="text-xs">Seminar Name *</Label>
                <Input
                  value={entry.seminar_name}
                  onChange={(e) => updateEntry(idx, 'seminar_name', e.target.value)}
                  placeholder="e.g., Spring Karate Camp 2024"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Instructor</Label>
                  <Input
                    value={entry.instructor}
                    onChange={(e) => updateEntry(idx, 'instructor', e.target.value)}
                    placeholder="e.g., Hanshi Jim Rivera"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Host Dojo</Label>
                  <Input
                    value={entry.host_dojo}
                    onChange={(e) => updateEntry(idx, 'host_dojo', e.target.value)}
                    placeholder="If not Owaza"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={entry.seminar_date}
                    onChange={(e) => updateEntry(idx, 'seminar_date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Hours</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={entry.hours}
                    onChange={(e) => updateEntry(idx, 'hours', e.target.value)}
                    placeholder="e.g., 6"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Topics Covered</Label>
                <Textarea
                  value={entry.topics}
                  onChange={(e) => updateEntry(idx, 'topics', e.target.value)}
                  placeholder="What was covered..."
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Certificate URL</Label>
                <Input
                  value={entry.certificate_url}
                  onChange={(e) => updateEntry(idx, 'certificate_url', e.target.value)}
                  placeholder="Link to certificate of completion"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={entry.notes}
                  onChange={(e) => updateEntry(idx, 'notes', e.target.value)}
                  placeholder="Any other details..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={addEntry}>
          <Plus className="h-4 w-4 mr-1" /> Add Another Seminar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !entries.some(e => e.seminar_name.trim())}
          className="bg-[#9B1B30] hover:bg-[#7d1626] text-white"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </div>

      {success && (
        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle className="h-4 w-4" /> Submitted successfully. Pending review.
        </p>
      )}
      {error && <SubmissionErrorBanner message={error} />}
    </div>
  );
};

// ============================================
// Existing Submission Card
// ============================================
const ExistingSubmissionCard = ({ submission, studentId, onWithdraw }) => {
  const { isOnline } = useConnectivity();
  const [withdrawing, setWithdrawing] = useState(false);

  const handleWithdraw = async () => {
    if (!isOnline) {
      alert('You are offline. Reconnect to withdraw this submission.');
      return;
    }
    if (!window.confirm('Withdraw this submission? This cannot be undone.')) return;

    setWithdrawing(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/history-submissions/${submission.id}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (response.ok) {
        onWithdraw();
      }
    } catch (error) {
      console.error('Failed to withdraw submission:', error);
    } finally {
      setWithdrawing(false);
    }
  };

  const dataField = submission[submission.submission_type];
  const entryCount = Array.isArray(dataField) ? dataField.length : 0;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/50">
      <div className="flex items-center gap-3">
        <StatusBadge status={submission.status} />
        <span className="text-sm text-stone-600 dark:text-stone-400">
          {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
        </span>
        <span className="text-xs text-stone-400">
          {new Date(submission.created_at).toLocaleDateString()}
        </span>
      </div>
      {submission.status === 'pending' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleWithdraw}
          disabled={withdrawing || !isOnline}
              title={!isOnline ? 'Reconnect to take action.' : undefined}
          className="text-red-500 hover:text-red-700 text-xs"
        >
          {withdrawing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
          Withdraw
        </Button>
      )}
      {submission.status === 'rejected' && submission.reviewer_notes && (
        <span className="text-xs text-red-500 max-w-[200px] truncate" title={submission.reviewer_notes}>
          {submission.reviewer_notes}
        </span>
      )}
    </div>
  );
};

// ============================================
// Main Form Component
// ============================================
const StudentHistoryForm = ({ studentId, open, onOpenChange }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('prior_experience');

  const fetchSubmissions = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/history-submissions`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (open && studentId) {
      fetchSubmissions();
    }
  }, [open, studentId, fetchSubmissions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-[#9B1B30]" />
            Share Your Martial Arts History
          </DialogTitle>
          <DialogDescription>
            Submit your prior experience, promotions, tournaments, or seminars for instructor review.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="prior_experience" className="text-xs sm:text-sm">
                <BookOpen className="h-4 w-4 mr-1 hidden sm:inline" />
                Prior Experience
              </TabsTrigger>
              <TabsTrigger value="promotion_history" className="text-xs sm:text-sm">
                <Award className="h-4 w-4 mr-1 hidden sm:inline" />
                Promotions
              </TabsTrigger>
              <TabsTrigger value="tournament_history" className="text-xs sm:text-sm">
                <Trophy className="h-4 w-4 mr-1 hidden sm:inline" />
                Tournaments
              </TabsTrigger>
              <TabsTrigger value="seminar_history" className="text-xs sm:text-sm">
                <GraduationCap className="h-4 w-4 mr-1 hidden sm:inline" />
                Seminars
              </TabsTrigger>
            </TabsList>

            <TabsContent value="prior_experience" className="mt-4">
              <PriorExperienceTab
                studentId={studentId}
                submissions={submissions}
                onSubmitSuccess={fetchSubmissions}
              />
            </TabsContent>

            <TabsContent value="promotion_history" className="mt-4">
              <PromotionHistoryTab
                studentId={studentId}
                submissions={submissions}
                onSubmitSuccess={fetchSubmissions}
              />
            </TabsContent>

            <TabsContent value="tournament_history" className="mt-4">
              <TournamentHistoryTab
                studentId={studentId}
                submissions={submissions}
                onSubmitSuccess={fetchSubmissions}
              />
            </TabsContent>

            <TabsContent value="seminar_history" className="mt-4">
              <SeminarHistoryTab
                studentId={studentId}
                submissions={submissions}
                onSubmitSuccess={fetchSubmissions}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StudentHistoryForm;
